'use client'

import { cn } from '@brew/ui/cn'
import { MOTION_TRANSITION } from '@brew/ui/motion'
import { ArrowRight, ChevronDown, type LucideIcon } from 'lucide-react'
import {
  AnimatePresence,
  LayoutGroup,
  MotionConfig,
  motion,
} from 'motion/react'
import Image, { getImageProps } from 'next/image'
import Link from 'next/link'
import {
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { preload } from 'react-dom'
import { slugify } from '@/lib/shared/text/slugify'
import { GoldenIcon } from '../home/hero/golden-word'

export type DropdownNavLink = {
  label: string
  href: string
  description?: string
  icon?: LucideIcon
  /** Opens in a new tab (docs, other sites). */
  external?: boolean
}

export type DropdownNavGroup = {
  /** Omitted for a menu's main list. */
  title?: string
  /** `list`: icon, label and description rows; `compact`: a 2-up grid of labels. */
  layout?: 'list' | 'compact'
  items: ReadonlyArray<DropdownNavLink>
}

type NavImage = { src: string; width: number; height: number }

/**
 * The picture on a menu's feature card: three emails on a still of the
 * golden-hour sky (`backdrop`, a few KB: the menus ship on every public
 * page), fanned (`fan`, a gallery) or stacked in order (`stack`, a series).
 */
export type DropdownNavVisual = {
  kind: 'fan' | 'stack'
  images: readonly [NavImage, NavImage, NavImage]
  backdrop: string
}

export type DropdownNavFeature = {
  title: string
  description: string
  href: string
  visual: DropdownNavVisual
}

export type DropdownNavMenu = {
  feature?: DropdownNavFeature
  groups: ReadonlyArray<DropdownNavGroup>
}

/** A top-level entry: a plain link, or a trigger that opens a menu. */
export type DropdownNavItem =
  | { label: string; href: string; external?: boolean; menu?: undefined }
  | { label: string; menu: DropdownNavMenu; href?: undefined }

export type DropdownNavigationProps = {
  items: ReadonlyArray<DropdownNavItem>
  className?: string
}

const TRIGGER =
  'relative flex cursor-pointer items-center justify-center gap-1 rounded-full px-3.5 py-1.5 text-[14px] text-mk-body leading-5 tracking-[-0.01em] transition-colors duration-quick hover:text-mk-ink focus-visible:outline-2 focus-visible:outline-mk-accent focus-visible:outline-offset-2'

const FOCUS =
  'focus-visible:outline-2 focus-visible:outline-mk-accent focus-visible:outline-offset-2'

function externalProps(external: boolean | undefined) {
  return external ? { target: '_blank', rel: 'noopener noreferrer' } : {}
}

/** A pointer that hovers — touch taps open menus by click instead. */
function isMouse(event: PointerEvent) {
  return event.pointerType === 'mouse'
}

/**
 * How long a mouse may be outside every entry before its menu closes. Long
 * enough to cross the few pixels between two triggers, short enough that
 * leaving the nav still feels immediate.
 */
const LEAVE_GRACE_MS = 120

/**
 * Menu-dropdown recipe: the panel grows from its trigger (the top centre),
 * 250ms in from 0.97, 150ms out to 0.99 with no travel. Crossing straight
 * from one trigger to the next swaps panels instead (`switching`): the old
 * one vanishes at once and the new one is opaque from its first frame
 * while it morphs from the old one's box, so the two never overlap half
 * faded, which read as a flicker.
 */
const PANEL_VARIANTS = {
  hidden: { opacity: 0, y: -6, scale: 0.97 },
  shown: { opacity: 1, y: 0, scale: 1 },
  gone: (switching: boolean) =>
    switching
      ? { opacity: 0, transition: { duration: 0 } }
      : { opacity: 0, scale: 0.99, transition: MOTION_TRANSITION.surfaceClose },
}

/** The `sizes` each feature-card picture renders with, so the preload and
 *  the `<Image>` resolve to the same optimized candidate. */
const BACKDROP_SIZES = '248px'
const THUMB_SIZES = '84px'

function preloadImage(props: { src: string; srcSet?: string; sizes?: string }) {
  preload(props.src, {
    as: 'image',
    fetchPriority: 'low',
    ...(props.srcSet ? { imageSrcSet: props.srcSet } : {}),
    ...(props.sizes ? { imageSizes: props.sizes } : {}),
  })
}

/**
 * Warm the browser cache with every menu's feature pictures once the nav
 * has mounted, so the first hover paints the card without a fetch. A menu
 * panel only exists while it is open, so without this its four images
 * (backdrop + three emails) load on the first open and flash in late.
 * Low fetch priority keeps them behind the page's own images.
 */
function preloadMenuImages(items: ReadonlyArray<DropdownNavItem>) {
  for (const item of items) {
    const visual = item.menu?.feature?.visual
    if (!visual) {
      continue
    }
    preloadImage(
      getImageProps({
        src: visual.backdrop,
        alt: '',
        fill: true,
        sizes: BACKDROP_SIZES,
      }).props
    )
    for (const image of visual.images) {
      preloadImage(
        getImageProps({
          src: image.src,
          alt: '',
          width: image.width,
          height: image.height,
          sizes: THUMB_SIZES,
        }).props
      )
    }
  }
}

/**
 * Runs `onShown` once, as soon as `element` is on screen: right away for a
 * nav that is showing, or later for one that starts hidden (the public
 * navbar hides this nav below `lg`, where its menus can never open) and
 * appears when the window widens. A browser without IntersectionObserver
 * runs it right away. Returns the cleanup.
 */
function whenOnScreen(element: Element, onShown: () => void): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    onShown()
    return () => undefined
  }
  const observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      observer.disconnect()
      onShown()
    }
  })
  observer.observe(element)
  return () => observer.disconnect()
}

/**
 * A horizontal nav whose entries open menus on hover (a tap or Enter toggles
 * them; a mouse click keeps a hovered menu open). One neutral pill slides
 * between hovered entries and one panel morphs between open menus (shared
 * `layoutId`s, scoped per instance by `LayoutGroup`). Escape closes a menu
 * and returns focus to its trigger; focus leaving an entry closes it.
 */
export function DropdownNavigation({
  items,
  className,
}: DropdownNavigationProps) {
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [hovered, setHovered] = useState<string | null>(null)
  // The pointer that pressed a trigger, so its click can tell a mouse (the
  // menu is already open from hover) from a tap or a keypress (toggle).
  const pressedWith = useRef('')
  const id = useId()
  const highlighted = hovered ?? openMenu
  // The pending close from a pointer leaving an entry, and which entry. It
  // waits a beat so a mouse crossing straight into the next entry swaps
  // menus (the panel and the pill morph across) instead of closing and
  // reopening with a flicker.
  const pendingLeave = useRef<{
    label: string
    timer: ReturnType<typeof setTimeout>
  } | null>(null)
  // Whether the menu now opening replaces one already open (a mouse crossing
  // from trigger to trigger), which swaps the panels instead of fading.
  const switching = useRef(false)

  const cancelLeave = () => {
    if (pendingLeave.current !== null) {
      clearTimeout(pendingLeave.current.timer)
      pendingLeave.current = null
    }
  }
  const leaveAfterGrace = (label: string) => {
    cancelLeave()
    pendingLeave.current = {
      label,
      timer: setTimeout(() => {
        pendingLeave.current = null
        switching.current = false
        setHovered((current) => (current === label ? null : current))
        setOpenMenu((open) => (open === label ? null : open))
      }, LEAVE_GRACE_MS),
    }
  }
  // Focus arriving in an entry whose close is pending (the mouse just left
  // and the keyboard took over) keeps its menu open: the close is cancelled,
  // and only the hover, which did end, is cleared.
  const keepOpenForFocus = (label: string) => {
    if (pendingLeave.current?.label !== label) {
      return
    }
    cancelLeave()
    setHovered((current) => (current === label ? null : current))
  }

  useEffect(
    () => () => {
      if (pendingLeave.current !== null) {
        clearTimeout(pendingLeave.current.timer)
      }
    },
    []
  )

  // Phones never see this nav, so its pictures wait until it is on screen.
  const navRef = useRef<HTMLUListElement>(null)
  useEffect(() => {
    const nav = navRef.current
    if (!nav) {
      return
    }
    return whenOnScreen(nav, () => preloadMenuImages(items))
  }, [items])

  return (
    <MotionConfig reducedMotion="user">
      <LayoutGroup id={id}>
        <ul
          ref={navRef}
          className={cn('relative flex items-center font-mk-body', className)}
        >
          {items.map((item, index) => {
            const isOpen = openMenu === item.label
            const menuId = `${id}-menu-${index}`

            const closeOnBlur = (event: FocusEvent<HTMLLIElement>) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                switching.current = false
                setOpenMenu((open) => (open === item.label ? null : open))
              }
            }
            const toggle = (event: MouseEvent<HTMLButtonElement>) => {
              const isMouseClick =
                event.detail !== 0 && pressedWith.current === 'mouse'
              switching.current = false
              setOpenMenu(isOpen && !isMouseClick ? null : item.label)
            }
            const closeOnEscape = (event: KeyboardEvent<HTMLLIElement>) => {
              if (event.key !== 'Escape' || !isOpen) {
                return
              }
              switching.current = false
              setOpenMenu(null)
              event.currentTarget.querySelector('button')?.focus()
            }

            return (
              <li
                key={item.label}
                className="relative"
                onPointerEnter={(event) => {
                  if (!isMouse(event)) {
                    return
                  }
                  cancelLeave()
                  switching.current =
                    item.menu !== undefined &&
                    openMenu !== null &&
                    openMenu !== item.label
                  setHovered(item.label)
                  setOpenMenu(item.menu ? item.label : null)
                }}
                onPointerLeave={(event) => {
                  if (!isMouse(event)) {
                    return
                  }
                  leaveAfterGrace(item.label)
                }}
                onFocus={() => keepOpenForFocus(item.label)}
                onBlur={closeOnBlur}
                onKeyDown={closeOnEscape}
              >
                {item.menu ? (
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={menuId}
                    onPointerDown={(event) => {
                      pressedWith.current = event.pointerType
                    }}
                    onClick={toggle}
                    className={cn(TRIGGER, { 'text-mk-ink': isOpen })}
                  >
                    <HoverPill isShown={highlighted === item.label} />
                    <span className="relative">{item.label}</span>
                    <ChevronDown
                      aria-hidden="true"
                      strokeWidth={1.75}
                      className={cn(
                        'relative size-3.5 opacity-60 transition-transform duration-fast ease-smooth-out',
                        { 'rotate-180': isOpen }
                      )}
                    />
                  </button>
                ) : (
                  <Link
                    href={item.href}
                    prefetch={!item.external}
                    className={TRIGGER}
                    {...externalProps(item.external)}
                  >
                    <HoverPill isShown={highlighted === item.label} />
                    <span className="relative">{item.label}</span>
                  </Link>
                )}

                {/* `custom` reaches the panel on its way out, so a panel
                    that is being replaced knows to vanish at once. */}
                <AnimatePresence custom={switching.current}>
                  {isOpen && item.menu ? (
                    <div
                      id={menuId}
                      className="absolute top-full left-1/2 z-50 -translate-x-1/2 pt-3"
                    >
                      <motion.div
                        layoutId="menu"
                        // The swap handles opacity itself: the shared-layout
                        // crossfade would hold the new panel part-faded for
                        // the whole morph.
                        layoutCrossfade={false}
                        variants={PANEL_VARIANTS}
                        custom={switching.current}
                        initial={switching.current ? false : 'hidden'}
                        animate="shown"
                        exit="gone"
                        transition={{
                          ...MOTION_TRANSITION.surfaceOpen,
                          layout: MOTION_TRANSITION.resize,
                        }}
                        className="w-max overflow-clip border border-mk-card-line bg-mk-card p-2 shadow-[0_24px_48px_-20px_rgb(64_40_16/0.22),0_2px_6px_rgb(64_40_16/0.05)] dark:shadow-[0_24px_48px_-16px_rgb(0_0_0/0.6)]"
                        // originX/Y, not transformOrigin: layout projection
                        // owns the origin of a layoutId element and only
                        // honours these.
                        style={{ borderRadius: 20, originX: 0.5, originY: 0 }}
                      >
                        <MenuPanel
                          menu={item.menu}
                          onNavigate={() => {
                            switching.current = false
                            setOpenMenu(null)
                          }}
                        />
                      </motion.div>
                    </div>
                  ) : null}
                </AnimatePresence>
              </li>
            )
          })}
        </ul>
      </LayoutGroup>
    </MotionConfig>
  )
}

/** The neutral pill that slides to whichever entry is hovered or open. */
function HoverPill({ isShown }: { isShown: boolean }) {
  if (!isShown) {
    return null
  }
  return (
    <motion.span
      aria-hidden="true"
      layoutId="hover-bg"
      className="absolute inset-0 bg-mk-ink/[0.05] dark:bg-white/[0.08]"
      style={{ borderRadius: 99 }}
      transition={MOTION_TRANSITION.slidingIndicator}
    />
  )
}

/** A feature card on the left, the menu's link groups on the right. */
function MenuPanel({
  menu,
  onNavigate,
}: {
  menu: DropdownNavMenu
  onNavigate: () => void
}) {
  return (
    <motion.div
      layout
      transition={{ layout: MOTION_TRANSITION.resize }}
      className="flex gap-2"
    >
      {menu.feature ? (
        <FeatureCard feature={menu.feature} onNavigate={onNavigate} />
      ) : null}
      <div className="flex w-[320px] flex-col gap-1 py-1">
        {menu.groups.map((group, index) => (
          <MenuGroup
            key={group.title ?? index}
            group={group}
            hasRule={index > 0}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </motion.div>
  )
}

function FeatureCard({
  feature,
  onNavigate,
}: {
  feature: DropdownNavFeature
  onNavigate: () => void
}) {
  return (
    <Link
      href={feature.href}
      prefetch={true}
      onClick={onNavigate}
      className={cn(
        'group/feature flex w-[248px] shrink-0 flex-col rounded-[14px]',
        FOCUS
      )}
    >
      <FeatureVisual visual={feature.visual} />
      <span className="flex shrink-0 flex-col gap-1 px-1 pt-3.5 pb-1">
        <span className="flex items-center gap-1.5 font-medium text-[14px] text-mk-ink leading-5 tracking-[-0.01em]">
          {feature.title}
          <ArrowRight
            aria-hidden="true"
            strokeWidth={1.75}
            className="size-3.5 -translate-x-1 text-mk-muted opacity-0 transition-[opacity,translate] duration-fast ease-smooth-out group-hover/feature:translate-x-0 group-hover/feature:opacity-100"
          />
        </span>
        <span className="text-[13px] text-mk-muted leading-[18px]">
          {feature.description}
        </span>
      </span>
    </Link>
  )
}

/** Fill the feature column while keeping the email composition centered. */
function FeatureVisual({ visual }: { visual: DropdownNavVisual }) {
  const [first, second, third] = visual.images
  return (
    <span className="relative block min-h-[156px] flex-1 overflow-clip rounded-[14px]">
      <Image
        src={visual.backdrop}
        alt=""
        fill
        sizes={BACKDROP_SIZES}
        className="object-cover"
      />
      <span className="absolute inset-x-0 top-1/2 h-[156px] -translate-y-1/2">
        {visual.kind === 'fan' ? (
          <>
            <EmailThumb
              image={first}
              className="top-7 left-[34px] -rotate-6 group-hover/feature:-translate-x-1 group-hover/feature:-rotate-8"
            />
            <EmailThumb
              image={third}
              className="top-7 right-[34px] rotate-6 group-hover/feature:translate-x-1 group-hover/feature:rotate-8"
            />
            <EmailThumb
              image={second}
              className="top-4 left-1/2 z-10 -translate-x-1/2 group-hover/feature:-translate-y-1"
            />
          </>
        ) : (
          // A series, first email in front: each later one sits up and to
          // the right behind it, and the stack spreads on hover.
          <>
            <EmailThumb
              image={third}
              className="top-3 left-[118px] scale-90 group-hover/feature:translate-x-2"
            />
            <EmailThumb
              image={second}
              className="top-[18px] left-[90px] z-10 scale-95 group-hover/feature:translate-x-1"
            />
            <EmailThumb
              image={first}
              className="top-6 left-[62px] z-20 group-hover/feature:-translate-y-1"
            />
          </>
        )}
      </span>
    </span>
  )
}

function EmailThumb({
  image,
  className,
}: {
  image: NavImage
  className: string
}) {
  return (
    <Image
      src={image.src}
      alt=""
      width={image.width}
      height={image.height}
      sizes={THUMB_SIZES}
      className={cn(
        'absolute h-auto w-[84px] rounded-md shadow-[0_8px_20px_-6px_rgb(64_24_0/0.45)] ring-1 ring-black/5 transition-transform duration-fast ease-smooth-out',
        className
      )}
    />
  )
}

function MenuGroup({
  group,
  hasRule,
  onNavigate,
}: {
  group: DropdownNavGroup
  hasRule: boolean
  onNavigate: () => void
}) {
  return (
    <div
      className={cn('flex flex-col', {
        'mt-1 border-mk-line/70 border-t pt-2': hasRule,
      })}
    >
      {group.title ? (
        <p className="px-3 pt-1 pb-1.5 font-medium text-[12px] text-mk-muted leading-4">
          {group.title}
        </p>
      ) : null}
      {group.layout === 'compact' ? (
        <ul className="grid grid-cols-2 gap-x-1">
          {group.items.map((link) => (
            <li key={link.href}>
              <Link
                href={link.href}
                prefetch={!link.external}
                onClick={onNavigate}
                className={cn(
                  'flex rounded-lg px-3 py-1.5 text-[13px] text-mk-body leading-5 transition-colors duration-quick hover:bg-mk-tile hover:text-mk-ink',
                  FOCUS
                )}
                {...externalProps(link.external)}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="flex flex-col">
          {group.items.map((link) => (
            <li key={link.href}>
              <MenuLink link={link} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function MenuLink({
  link,
  onNavigate,
}: {
  link: DropdownNavLink
  onNavigate: () => void
}) {
  const Icon = link.icon
  return (
    <Link
      href={link.href}
      prefetch={!link.external}
      onClick={onNavigate}
      className={cn(
        'group/link flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors duration-quick hover:bg-mk-tile',
        FOCUS
      )}
      {...externalProps(link.external)}
    >
      {Icon ? (
        // The page hero's icon treatment at menu size: the icon itself in the
        // golden-hour fill, no tile (a static gradient: the menus ship on
        // every public page).
        <span className="flex size-8 shrink-0 items-center justify-center">
          <GoldenIcon
            icon={Icon}
            id={`nav-icon-${slugify(link.label)}`}
            fill="gradient"
            className="size-6"
          />
        </span>
      ) : null}
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="font-medium text-[14px] text-mk-ink leading-5 tracking-[-0.01em]">
          {link.label}
        </span>
        {link.description ? (
          <span className="text-[13px] text-mk-muted leading-[18px]">
            {link.description}
          </span>
        ) : null}
      </span>
    </Link>
  )
}
